import React, { useState } from 'react';
import { useInventory } from '../context/InventoryContext';
import { Package, ArrowRightLeft, AlertTriangle } from 'lucide-react';

const formatDateDMY = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Dashboard() {
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const { getStockReport, transactions } = useInventory();
  const report = getStockReport(reportDate);

  const totalItems = report.length;
  const lowStockItems = report.filter(i => i.totalStock <= i.minLimit).length;
  const todayTrans = transactions.filter(t => t.date.startsWith(reportDate)).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-4">
          <div className="p-3 bg-blue-100 text-blue-600 rounded-lg"><Package size={24} /></div>
          <div><p className="text-sm text-slate-500">إجمالي الأصناف المسجلة</p><p className="text-2xl font-bold">{totalItems}</p></div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-4">
          <div className="p-3 bg-red-100 text-red-600 rounded-lg"><AlertTriangle size={24} /></div>
          <div><p className="text-sm text-slate-500">أصناف تجاوزت الحد الأدنى</p><p className="text-2xl font-bold text-red-600">{lowStockItems}</p></div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-4">
          <div className="p-3 bg-green-100 text-green-600 rounded-lg"><ArrowRightLeft size={24} /></div>
          <div><p className="text-sm text-slate-500">حركات تمت في هذا التاريخ</p><p className="text-2xl font-bold">{todayTrans}</p></div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-xl font-bold">دفتر الأرصدة (الاستاذ)</h2>
        <div className="flex items-center gap-3">
          <div className="bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg font-mono text-sm font-bold text-blue-800">
            تاريخ التقرير: <span className="text-blue-600 text-base">{formatDateDMY(reportDate)}</span>
          </div>
          <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl shadow-sm border">
        <table className="w-full text-right text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th colSpan="4" className="p-2 border-l"></th>
              <th colSpan="3" className="p-2 text-center border-l bg-emerald-50 text-emerald-800 font-bold">إجمالي الوارد (+)</th>
              <th colSpan="2" className="p-2 text-center border-l bg-rose-50 text-rose-800 font-bold">إجمالي المنصرف (-)</th>
              <th className="p-2"></th>
            </tr>
            <tr className="border-b">
              <th className="p-3 whitespace-nowrap">الصنف</th>
              <th className="p-3 text-center whitespace-nowrap bg-slate-100 text-slate-700 font-bold">الحد الأدنى</th>
              <th className="p-3 text-center whitespace-nowrap">الرصيد الفعلي</th>
              <th className="p-3 text-center text-blue-600 border-l whitespace-nowrap">WIP (صالة إنتاج)</th>
              
              <th className="p-3 text-center text-emerald-600 whitespace-nowrap bg-emerald-50/30">مشتريات</th>
              <th className="p-3 text-center text-emerald-600 whitespace-nowrap bg-emerald-50/30">إنتاج</th>
              <th className="p-3 text-center text-emerald-600 border-l whitespace-nowrap bg-emerald-50/30">مرتجعات</th>
              
              <th className="p-3 text-center text-rose-600 whitespace-nowrap bg-rose-50/30">صرف مبيعات</th>
              <th className="p-3 text-center text-orange-600 border-l whitespace-nowrap bg-rose-50/30">مستخدم بتصنيع</th>
              
              <th className="p-3 text-center text-slate-500 whitespace-nowrap">تفاصيل المخازن</th>
            </tr>
          </thead>
          <tbody>
            {report.map(item => {
              const isLow = item.totalStock <= item.minLimit && item.minLimit > 0;
              return (
                <tr key={item.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-bold">{item.name} <span className="text-[10px] text-slate-400">({item.unit})</span></td>
                  <td className="p-3 text-center font-mono font-bold text-slate-600 bg-slate-50/80">{item.minLimit ?? 0}</td>
                  <td className={`p-3 text-center font-bold text-base ${isLow ? 'text-red-600 font-black bg-red-50/60' : ''}`}>
                    {item.totalStock}
                    {isLow && <span className="text-[10px] block text-red-500 font-normal">⚠️ رصيد منخفض</span>}
                  </td>
                  <td className="p-3 text-center bg-blue-50/50 border-l">{item.wipStock}</td>
                  
                  <td className="p-3 text-center text-emerald-700">{item.purchasesQty || '-'}</td>
                  <td className="p-3 text-center text-emerald-700">{item.productionYieldQty || '-'}</td>
                  <td className="p-3 text-center text-emerald-700 border-l">{item.returnsQty || '-'}</td>
                  
                  <td className="p-3 text-center text-rose-700">{item.salesQty || '-'}</td>
                  <td className="p-3 text-center text-orange-600 border-l font-bold">{item.bomQty || '-'}</td>
                  
                  <td className="p-3 text-center bg-slate-50 font-mono">
                    {Object.entries(item.breakdown).map(([wid, qty]) => (qty !== 0 && <div key={wid} className="text-[10px]">{wid}: {qty}</div>))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
